import { generateClient } from 'aws-amplify/api';

import { format, fromUnixTime, isAfter, add, isBefore, addHours } from 'date-fns';
import { useCookies } from 'vue3-cookies';
import * as subscriptions from '@/amplifyGraphql/subscriptions';

import { frenchFormatedDate } from './utils';

import store from '@/store/index.js';

const client = generateClient();

const isCancelable = (maxLimitInHoursBeforeCancelation, startTimestamp, endTimestamp, isInLimit, isAnulationPossible) => {
  if (isBefore(new Date(startTimestamp), new Date())) return false;

  let response = (isInLimit && isAnulationPossible);
  if (maxLimitInHoursBeforeCancelation !== null) {
    const limit = addHours(new Date(), maxLimitInHoursBeforeCancelation);
    const end_date = new Date(endTimestamp);
    response = isBefore(limit, end_date);
  }

  return response;
};

const computeReservationBody = (session, participant, siteInfo) => {
  let maxLimitMinutes = siteInfo.perso_settings.maxLimitInMinutesBeforeCancelation;
  if (!maxLimitMinutes && maxLimitMinutes !== 0 && siteInfo.perso_settings.maxLimitInHoursBeforeCancelation) {
    maxLimitMinutes = siteInfo.perso_settings.maxLimitInHoursBeforeCancelation * 60;
  }
  const isInLimit = maxLimitMinutes ? !isAfter(fromUnixTime(session.start_time), add(new Date(), { minutes: maxLimitMinutes })) : true;

  const isAnulationPossible = !siteInfo.is_internal_session;

  return {
    billet_type: siteInfo.is_internal_session ? 'single-session' : 'reloadable',
    qrcode: participant.qrcode,
    contractNumber: participant.contract_number,
    product: null,
    product_uuid: null,
    type: session.name,
    activityCode: session.activity,
    participants: session.participants,
    startTimestamp: parseFloat(frenchFormatedDate(null, fromUnixTime(session.start_time), 'T')),
    endTimestamp: parseFloat(frenchFormatedDate(null, fromUnixTime(session.end_time), 'T')),
    code: session.id,
    isSingleSession: siteInfo.is_internal_session,
    isAnulationPossible: isAnulationPossible,
    isInLimit: isInLimit,
    startTime: frenchFormatedDate(null, fromUnixTime(session.start_time), "HH'h'mm"),
    endTime: frenchFormatedDate(null, fromUnixTime(session.end_time), "HH'h'mm"),
    horse: participant.horse ? participant.horse.wtitle : null,
    isCancelable: isCancelable(null, parseFloat(frenchFormatedDate(null, fromUnixTime(session.start_time), 'T')), parseFloat(frenchFormatedDate(null, fromUnixTime(session.end_time), 'T')), isInLimit, isAnulationPossible),
    startDate: frenchFormatedDate(null, fromUnixTime(session.start_time), 'dd/MM/yyyy'),
    endDate: frenchFormatedDate(null, fromUnixTime(session.end_time), 'dd/MM/yyyy')
  };
};

const handleAddParticipantToHoranetSession = data => {
  const response = data.onAddParticipantsToSession;

  const { contacts, siteInfo } = store.state;
  const field = siteInfo.is_internal_session ? 'uuid' : 'horanet_id';

  if (response && response.success) {
    const body = JSON.parse(response.data);

    let participantReservations = [...store.state.reservations];

    body.map(session => {
      if (!siteInfo) {
        return;
      }
      if ((siteInfo.code_site_comptage && session.codeSiteComptage && session.codeSiteComptage.toString() === siteInfo.code_site_comptage.toString()) || (siteInfo.workspace && session.workspace && siteInfo.workspace.toUpperCase() === session.workspace.toUpperCase())) {
        session.participants.map(part => {
          participantReservations = participantReservations.map(p => {
            const participantInfo = contacts.find(c => c.uuid === p.uuid);

            const reservationExist = p.sessions.some(s => s.code === session.id);

            if (!reservationExist && part.uuid.includes(participantInfo[field])) {
              const sessionBody = computeReservationBody(session, part, siteInfo);

              p.sessions.push(sessionBody);
            }

            p.sessions = p.sessions.sort((a, b) => {
              let compare = a.startTimestamp - b.startTimestamp;
              if (!compare) {
                compare = a.endTimestamp - b.endTimestamp;
                if (!compare) {
                  compare = a.type.localeCompare(b.type);
                }
              }

              return compare;
            });
            return p;
          });
        });
      }
    });

    store.commit('setReservations', participantReservations);
  }
};

const handleDeleteParticipantFromHoranetSession = data => {
  const response = data.onDeleteParticipantsFromSession;

  const { contacts, siteInfo } = store.state;
  const field = siteInfo.is_internal_session ? 'uuid' : 'horanet_id';

  if (response && response.success) {
    const body = JSON.parse(response.data);

    // participants is the current session participants
    let participantReservations = [...store.state.reservations];
    body.forEach(session => {
      participantReservations = participantReservations.map(p => {
        const participantInfo = contacts.find(c => c.uuid === p.uuid);
        p.sessions = (p.sessions || []).filter(s => session && session.participants.some(part => part.uuid.includes(participantInfo[field]) && session.id.toString() === s.code.toString()));

        return p;
      });
    });
    store.commit('setReservations', participantReservations);
  }
};

const handleInternalParticipant = sessions => {
  const { siteInfo } = store.state;

  let participantReservations = [...store.state.reservations];

  sessions.map(session => {
    const sessionParticipants = JSON.parse(session.participants || []);
    if (session.workspace && siteInfo.workspace && session.workspace.toLowerCase() === siteInfo.workspace) {
      participantReservations = participantReservations.map(p => {
        const sessionParticipant = sessionParticipants.find(part => part.uuid.includes(p.uuid));

        const reservationExist = p.sessions.some(s => s.code === session.id);

        if (sessionParticipant && !reservationExist) {
          const reservationBody = computeReservationBody(session, sessionParticipant, siteInfo);
          p.sessions.push(reservationBody);
        }

        if (reservationExist && !sessionParticipant) {
          p.sessions = p.sessions.filter(s => s.code !== session.id);
        }

        p.sessions = p.sessions.sort((a, b) => {
          let compare = a.startTimestamp - b.startTimestamp;
          if (!compare) {
            compare = a.endTimestamp - b.endTimestamp;
            if (!compare) {
              compare = a.type.localeCompare(b.type);
            }
          }

          return compare;
        });

        return p;
      });
    }
  });

  store.commit('setReservations', participantReservations);
};

const unsubscribe = async () => {
  const { amplifySubscription } = store.state;

  if (amplifySubscription) {
    Object.values(amplifySubscription).map(subscription => {
      subscription.unsubscribe();
    });

    store.commit('setAmplifySubscription', null);
  }
};

const handleSubscription = async () => {
  await unsubscribe();
  const amplifySubscriptions = {};

  const { cookies } = useCookies();

  const cookie = cookies.get('oauth2_cookie');

  amplifySubscriptions.addParticipant = client.graphql({ query: subscriptions.onAddParticipantsToSession, authToken: cookie }).subscribe({
    next: ({ data }) => {
      const response = data.onAddParticipantsToSession;

      if (response && response.success) {
        handleAddParticipantToHoranetSession(data);
      }
    },
    error: error => {
      console.warn(error);
    }
  });

  amplifySubscriptions.deleteParticipant = client.graphql({ query: subscriptions.onDeleteParticipantsFromSession, authToken: cookie }).subscribe({
    next: ({ data }) => {
      const response = data.onDeleteParticipantsFromSession;

      if (response && response.success) {
        handleDeleteParticipantFromHoranetSession(data);
      }
    },
    error: error => {
      console.warn(error);
    }
  });

  amplifySubscriptions.addInternalParticipant = client.graphql({ query: subscriptions.onHandleUpdateReplicaSessions, authToken: cookie }).subscribe({
    next: ({ data }) => {
      const response = data.onHandleUpdateReplicaSessions;

      if (response && response.edit && response.edit.length) {
        handleInternalParticipant(response.edit);
      }
    },
    error: error => {
      console.warn(error);
    }
  });

  amplifySubscriptions.updateSession = client.graphql({ query: subscriptions.onUpdateSession, variables: { filter: { workspace: { eq: store.state.siteInfo.workspace.toUpperCase() } } }, authToken: cookie }).subscribe({
    next: ({ data }) => {
      const response = data.onUpdateSession;
      handleInternalParticipant([response]);
    },
    error: error => {
      console.warn(error);
    }
  });

  // set store
  store.commit('setAmplifySubscription', amplifySubscriptions);
};

export { handleSubscription };
